import { useEffect, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'

/**
 * Минимальный popover: триггер-кнопка + панель снизу.
 * Закрывается по клику вне, Escape и при изменении `closeKey`.
 */
export function Popover({
  trigger,
  children,
  align = 'right',
  panelClassName,
  buttonClassName,
  ariaLabel,
}: {
  trigger: ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
  align?: 'left' | 'right'
  panelClassName?: string
  buttonClassName?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="stryzh-popover" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={clsx('stryzh-popover__trigger', open && 'stryzh-popover__trigger--open', buttonClassName)}
      >
        {trigger}
      </button>
      {open ? (
        <div
          role="dialog"
          className={clsx(
            'stryzh-popover__panel',
            align === 'left' && 'stryzh-popover__panel--left',
            panelClassName,
          )}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      ) : null}
    </div>
  )
}
